import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Grid,
  Paper,
  Box,
  Card,
  CardContent,
  CardHeader,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Assignment as InvestigationIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  GetApp as DownloadIcon
} from '@mui/icons-material';
import {
  fetchTsiTicketsFromAdo,
  getTsiTickets,
  getTsiTicketStats,
  getTsiTicketColumns,
  getTsiOpenVsClosedChart,
  getTsiClosedByClassificationChart,
  getTsiDailyIncomingByPriorityChart,
  getLinkedBugsForTsiTickets,
  getTsiTicketLinks,
  getAdoEmailGroups
} from '../../services/apiService';
import TsiCharts from '../charts/TsiCharts';

/**
 * TSI Dashboard component for investigation work items
 * @returns {JSX.Element} TSI Dashboard component
 */
const TsiDashboard = () => {
  // State management
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [columns, setColumns] = useState({ coreColumns: [], additionalColumns: [] });
  const [loading, setLoading] = useState(true);
  const [fetchingBugs, setFetchingBugs] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [linkedBugsMap, setLinkedBugsMap] = useState({}); // Map of ticketId -> linked bugs array

  // ADO fetch state. Prefilled with the TSI saved-query (folder) GUID so users can
  // fetch without pasting it each time; still editable for ad-hoc queries.
  const [adoQuery, setAdoQuery] = useState('de02c7ea-4576-4619-880c-e9309bf2f57f');
  const [fetchingAdo, setFetchingAdo] = useState(false);

  // Table state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  
  // Filter state
  const [filters, setFilters] = useState({
    state: [],
    team: '',
    priority: '',
    environment: [],
    createdMonthYear: '',
    closedMonthYear: '',
    group: ''
  });
  
  // ADO Email Groups state
  const [adoEmailGroups, setAdoEmailGroups] = useState({
    sreGroup: '',
    professionalServiceGroup: '',
    clientSupportTeam: ''
  });
  const [loadingEmailGroups, setLoadingEmailGroups] = useState(true);
  
  // Get current user from localStorage
  const getCurrentUser = () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      return user;
    } catch (error) {
      console.error('Error parsing user from localStorage:', error);
      return {};
    }
  };
  
  const currentUser = getCurrentUser();
  const isAdmin = currentUser.role === 'admin';
  
  // Fetch TSI tickets
  const fetchTickets = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Don't send group filter to backend yet (needs backend support)
      const backendFilters = { ...filters };
      delete backendFilters.group;
      
      const pagination = {
        page: page + 1, // API expects 1-based page numbers
        limit: rowsPerPage,
        sortBy: 'createdDate',
        sortOrder: 'desc'
      };
      
      const response = await getTsiTickets(backendFilters, pagination);
      
      if (response.success) {
        setTickets(response.data || []);
        setTotalCount(response.pagination?.totalCount || 0);
      } else {
        throw new Error(response.message || 'Failed to fetch TSI tickets');
      }
      
    } catch (err) {
      console.error('Error fetching TSI tickets:', err);
      setError(err.message || 'Failed to load TSI tickets');
    } finally {
      setLoading(false);
    }
  }, [filters, page, rowsPerPage]);
  
  // Fetch statistics
  const fetchStats = useCallback(async () => {
    try {
      const response = await getTsiTicketStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (err) {
      console.error('Error fetching TSI ticket stats:', err);
    }
  }, []);
  
  // Fetch available columns
  const fetchColumns = useCallback(async () => {
    try {
      const response = await getTsiTicketColumns();
      if (response.success) {
        setColumns(response.data);
      }
    } catch (err) {
      console.error('Error fetching TSI ticket columns:', err);
    }
  }, []);
  
  // Initial data load and reload when filters change
  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);
  
  // Load stats and columns only once
  useEffect(() => {
    fetchStats();
    fetchColumns();
  }, [fetchStats, fetchColumns]);
  
  // Fetch ADO email groups
  const fetchAdoEmailGroups = useCallback(async () => {
    try {
      setLoadingEmailGroups(true);
      const response = await getAdoEmailGroups();
      if (response.success && response.data) {
        setAdoEmailGroups({
          sreGroup: response.data.sreGroup || '',
          professionalServiceGroup: response.data.professionalServiceGroup || '',
          clientSupportTeam: response.data.clientSupportTeam || ''
        });
      }
    } catch (err) {
      console.error('Error fetching ADO email groups:', err);
    } finally {
      setLoadingEmailGroups(false);
    }
  }, []);
  
  // Load email groups on mount
  useEffect(() => {
    fetchAdoEmailGroups();
  }, [fetchAdoEmailGroups]);
  
  // Fetch TSI tickets from ADO using the entered query (saved-query GUID or WIQL).
  // Clears existing tickets, repopulates from ADO, then reloads the page so the
  // user sees the fresh data.
  const handleFetchFromAdo = async () => {
    if (!adoQuery.trim()) {
      setError('Enter an ADO query (saved-query GUID or WIQL) first');
      return;
    }

    try {
      setFetchingAdo(true);
      setError(null);

      const response = await fetchTsiTicketsFromAdo(adoQuery.trim());

      if (response.success) {
        setSuccess(`${response.message}. Refreshing…`);
        // Reload so all cards, charts and the table reflect the new dataset.
        setTimeout(() => window.location.reload(), 800);
      } else {
        throw new Error(response.message || 'Fetch failed');
      }

    } catch (err) {
      console.error('Error fetching TSI tickets from ADO:', err);
      setError(err.message || 'Failed to fetch TSI tickets from ADO');
      setFetchingAdo(false);
    }
  };

  // Handle get linked bugs
  const handleGetLinkedBugs = async () => {
    try {
      setFetchingBugs(true);
      setError(null);
      
      const response = await getLinkedBugsForTsiTickets();
      
      if (response.success) {
        setSuccess(`Successfully fetched linked bugs for ${response.successCount} tickets. Found ${response.totalBugsFound} total bugs.`);
        
        // Refresh tickets to get updated linked bugs data
        await fetchTickets();
      } else {
        throw new Error(response.message || 'Failed to fetch linked bugs');
      }
      
    } catch (err) {
      console.error('Error fetching linked bugs:', err);
      setError(err.message || 'Failed to fetch linked bugs');
    } finally {
      setFetchingBugs(false);
    }
  };

  // Fetch linked bugs for displayed tickets
  const fetchLinkedBugsForTickets = useCallback(async (ticketIds) => {
    if (!ticketIds || ticketIds.length === 0) return;
    
    try {
      const bugsPromises = ticketIds.map(async (ticketId) => {
        if (!ticketId) return null;
        try {
          const response = await getTsiTicketLinks(ticketId);
          return { ticketId, bugs: response.linkedBugs || [] };
        } catch (error) {
          console.error(`Error fetching linked bugs for ticket ${ticketId}:`, error);
          return { ticketId, bugs: [] };
        }
      });
      
      const results = await Promise.all(bugsPromises);
      const newLinkedBugsMap = {};
      results.forEach(result => {
        if (result) {
          newLinkedBugsMap[result.ticketId] = result.bugs;
        }
      });
      
      setLinkedBugsMap(prev => ({ ...prev, ...newLinkedBugsMap }));
    } catch (error) {
      console.error('Error fetching linked bugs:', error);
    }
  }, []);

  // Fetch linked bugs when tickets are loaded
  useEffect(() => {
    if (tickets.length > 0) {
      const ticketIds = tickets
        .map(t => t.id)
        .filter(id => id && !linkedBugsMap[id]); // Only fetch if not already loaded
      
      if (ticketIds.length > 0) {
        fetchLinkedBugsForTickets(ticketIds);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickets]); // Only depend on tickets, not linkedBugsMap to avoid infinite loops
  
  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setPage(0); // Reset to first page when filtering
  };
  
  // Clear filters
  const clearFilters = () => {
    setFilters({
      state: [],
      team: '',
      priority: '',
      environment: [],
      createdMonthYear: '',
      closedMonthYear: '',
      group: ''
    });
    setPage(0);
  };
  
  // Helper function to determine which group a ticket belongs to based on creator email
  // Note: This requires creator email to be available in the ticket or fetched from ADO
  const getTicketGroup = (ticket) => {
    // For now, we'll need to fetch creator email from ADO or add it to the ticket model
    // This is a placeholder function that will be enhanced with actual creator email lookup
    // The creator email should be compared against the email groups
    const creatorEmail = ticket.createdByEmail || ticket.additionalData?.createdByEmail || null;
    
    if (!creatorEmail) {
      return 'Other';
    }
    
    const emailLower = creatorEmail.toLowerCase().trim();
    
    // Check SRE Group
    if (adoEmailGroups.sreGroup) {
      const sreEmails = adoEmailGroups.sreGroup.split(',').map(e => e.trim().toLowerCase()).filter(e => e);
      if (sreEmails.some(email => emailLower.includes(email) || email.includes(emailLower))) {
        return 'SRE Group';
      }
    }
    
    // Check Professional Service Group
    if (adoEmailGroups.professionalServiceGroup) {
      const psEmails = adoEmailGroups.professionalServiceGroup.split(',').map(e => e.trim().toLowerCase()).filter(e => e);
      if (psEmails.some(email => emailLower.includes(email) || email.includes(emailLower))) {
        return 'Professional Service Group';
      }
    }
    
    // Check Client Support Team
    if (adoEmailGroups.clientSupportTeam) {
      const csEmails = adoEmailGroups.clientSupportTeam.split(',').map(e => e.trim().toLowerCase()).filter(e => e);
      if (csEmails.some(email => emailLower.includes(email) || email.includes(emailLower))) {
        return 'Client Support Team';
      }
    }
    
    return 'Other';
  };
  
  // Filter tickets by group
  const getFilteredTickets = () => {
    if (!filters.group || filters.group === '') {
      return tickets;
    }
    
    return tickets.filter(ticket => {
      const ticketGroup = getTicketGroup(ticket);
      return ticketGroup === filters.group;
    });
  };
  
  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      // Format as M/D/YYYY to match the data format
      return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
    } catch (error) {
      return 'Invalid Date';
    }
  };
  
  // Get unique values for filter dropdowns
  const getUniqueValues = (field) => {
    if (!stats?.distributions?.[field]) return [];
    return stats.distributions[field]
      .filter(item => item._id && item._id !== null)
      .map(item => item._id)
      .sort();
  };

  // Generate ADO URL for a ticket ID
  const getAdoUrl = (ticketId) => {
    if (!ticketId) return null;
    // Use the correct ADO base URL for HighQ Tech project
    return `https://tr-legal.visualstudio.com/HighQ%20Tech/_workitems/edit/${ticketId}`;
  };
  
  // Render statistics cards
  const renderStatsCards = () => {
    if (!stats) return null;
    
    return (
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Box 
                  display="flex" 
                  alignItems="center" 
                  justifyContent="center" 
                  bgcolor="#1D4434" 
                  color="white" 
                  borderRadius="50%" 
                  width={48} 
                  height={48} 
                  mr={2}
                >
                  <InvestigationIcon />
                </Box>
                <Box>
                  <Typography variant="h4">{stats.totalCount}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total TSI Tickets
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        {stats.ageStatistics && (
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Age Statistics</Typography>
                <Typography variant="body2" color="textSecondary">
                  Avg Age: {stats.ageStatistics.averageAge} days
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Min Age: {stats.ageStatistics.minimumAge} days
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Max Age: {stats.ageStatistics.maximumAge} days
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Closed Tickets: {stats.ageStatistics.totalClosedTickets}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
        
        {stats.latestUpload && (
          <Grid item xs={12} sm={6} md={stats.ageStatistics ? 6 : 9}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Latest Upload</Typography>
                <Typography variant="body2" color="textSecondary">
                  File: {stats.latestUpload.fileName}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Uploaded: {formatDate(stats.latestUpload.uploadDate)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  By: {stats.latestUpload.uploadedBy}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    );
  };
  
  // Render filters
  const renderFilters = () => (
    <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
      <Box display="flex" alignItems="center" mb={2}>
        <SearchIcon sx={{ mr: 1, color: "#1D4434" }} />
        <Typography variant="subtitle1">Filters</Typography>
        <Box flexGrow={1} />
        <Button
          startIcon={<ClearIcon />}
          onClick={clearFilters}
          size="small"
        >
          Clear Filters
        </Button>
      </Box>
      
      <Grid container spacing={1.5} sx={{ flexWrap: 'wrap' }}>
        <Grid item xs={12} sm="auto" sx={{ minWidth: '140px', flex: '1 1 0' }}>
          <FormControl fullWidth size="small">
            <InputLabel>State</InputLabel>
            <Select
              multiple
              value={filters.state}
              onChange={(e) => handleFilterChange('state', e.target.value)}
              label="State"
              renderValue={(selected) => 
                selected.length === 0 ? (
                  <Typography variant="body2" color="textSecondary">All States</Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip
                        key={value}
                        label={value}
                        size="small"
                        sx={{ 
                          height: 20, 
                          fontSize: '0.75rem',
                          '& .MuiChip-label': {
                            px: 1
                          }
                        }}
                      />
                    ))}
                  </Box>
                )
              }
            >
              {getUniqueValues('state').map(state => (
                <MenuItem key={state} value={state}>{state}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} sm="auto" sx={{ minWidth: '120px', flex: '1 1 0' }}>
          <FormControl fullWidth size="small">
            <InputLabel>Team</InputLabel>
            <Select
              value={filters.team}
              onChange={(e) => handleFilterChange('team', e.target.value)}
              label="Team"
            >
              <MenuItem value="">All</MenuItem>
              {getUniqueValues('team').map(team => (
                <MenuItem key={team} value={team}>{team}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} sm="auto" sx={{ minWidth: '140px', flex: '1 1 0' }}>
          <FormControl fullWidth size="small">
            <InputLabel>Environment</InputLabel>
            <Select
              multiple
              value={filters.environment}
              onChange={(e) => handleFilterChange('environment', e.target.value)}
              label="Environment"
              renderValue={(selected) => 
                selected.length === 0 ? (
                  <Typography variant="body2" color="textSecondary">All Environments</Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip
                        key={value}
                        label={value}
                        size="small"
                        sx={{ 
                          height: 20, 
                          fontSize: '0.75rem',
                          '& .MuiChip-label': {
                            px: 1
                          }
                        }}
                      />
                    ))}
                  </Box>
                )
              }
            >
              {getUniqueValues('environment').map(env => (
                <MenuItem key={env} value={env}>{env}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} sm="auto" sx={{ minWidth: '100px', flex: '0 0 auto' }}>
          <FormControl fullWidth size="small">
            <InputLabel>Priority</InputLabel>
            <Select
              value={filters.priority}
              onChange={(e) => handleFilterChange('priority', e.target.value)}
              label="Priority"
            >
              <MenuItem value="">All</MenuItem>
              {getUniqueValues('priority').map(priority => (
                <MenuItem key={priority} value={priority}>{priority}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} sm="auto" sx={{ minWidth: '130px', flex: '0 0 auto' }}>
          <FormControl fullWidth size="small">
            <InputLabel>Created Month</InputLabel>
            <Select
              value={filters.createdMonthYear}
              onChange={(e) => handleFilterChange('createdMonthYear', e.target.value)}
              label="Created Month"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Jul-24">Jul-24</MenuItem>
              <MenuItem value="Aug-24">Aug-24</MenuItem>
              <MenuItem value="Sep-24">Sep-24</MenuItem>
              <MenuItem value="Oct-24">Oct-24</MenuItem>
              <MenuItem value="Nov-24">Nov-24</MenuItem>
              <MenuItem value="Dec-24">Dec-24</MenuItem>
              <MenuItem value="Jan-25">Jan-25</MenuItem>
              <MenuItem value="Feb-25">Feb-25</MenuItem>
              <MenuItem value="Mar-25">Mar-25</MenuItem>
              <MenuItem value="Apr-25">Apr-25</MenuItem>
              <MenuItem value="May-25">May-25</MenuItem>
              <MenuItem value="Jun-25">Jun-25</MenuItem>
              <MenuItem value="Jul-25">Jul-25</MenuItem>
              <MenuItem value="Aug-25">Aug-25</MenuItem>
              <MenuItem value="Sep-25">Sep-25</MenuItem>
              <MenuItem value="Oct-25">Oct-25</MenuItem>
              <MenuItem value="Nov-25">Nov-25</MenuItem>
              <MenuItem value="Dec-25">Dec-25</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} sm="auto" sx={{ minWidth: '130px', flex: '0 0 auto' }}>
          <FormControl fullWidth size="small">
            <InputLabel>Closed Month</InputLabel>
            <Select
              value={filters.closedMonthYear}
              onChange={(e) => handleFilterChange('closedMonthYear', e.target.value)}
              label="Closed Month"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Jul-24">Jul-24</MenuItem>
              <MenuItem value="Aug-24">Aug-24</MenuItem>
              <MenuItem value="Sep-24">Sep-24</MenuItem>
              <MenuItem value="Oct-24">Oct-24</MenuItem>
              <MenuItem value="Nov-24">Nov-24</MenuItem>
              <MenuItem value="Dec-24">Dec-24</MenuItem>
              <MenuItem value="Jan-25">Jan-25</MenuItem>
              <MenuItem value="Feb-25">Feb-25</MenuItem>
              <MenuItem value="Mar-25">Mar-25</MenuItem>
              <MenuItem value="Apr-25">Apr-25</MenuItem>
              <MenuItem value="May-25">May-25</MenuItem>
              <MenuItem value="Jun-25">Jun-25</MenuItem>
              <MenuItem value="Jul-25">Jul-25</MenuItem>
              <MenuItem value="Aug-25">Aug-25</MenuItem>
              <MenuItem value="Sep-25">Sep-25</MenuItem>
              <MenuItem value="Oct-25">Oct-25</MenuItem>
              <MenuItem value="Nov-25">Nov-25</MenuItem>
              <MenuItem value="Dec-25">Dec-25</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} sm="auto" sx={{ minWidth: '160px', flex: '1 1 0' }}>
          <FormControl fullWidth size="small">
            <InputLabel>Group</InputLabel>
            <Select
              value={filters.group}
              onChange={(e) => handleFilterChange('group', e.target.value)}
              label="Group"
              disabled={loadingEmailGroups}
            >
              <MenuItem value="">All Groups</MenuItem>
              <MenuItem value="SRE Group">SRE Group</MenuItem>
              <MenuItem value="Professional Service Group">Professional Service Group</MenuItem>
              <MenuItem value="Client Support Team">Client Support Team</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </Paper>
  );
  
  // Render data table
  const renderDataTable = () => {
    const filteredTickets = getFilteredTickets();
    
    return (
      <Paper elevation={2}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Work Item Type</TableCell>
                <TableCell>State</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Team</TableCell>
                <TableCell>Environment</TableCell>
                <TableCell>Created Date</TableCell>
                <TableCell>Activated Date</TableCell>
                <TableCell>Closed Date</TableCell>
                <TableCell>Age (Days)</TableCell>
                <TableCell>Linked Bugs</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={12} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : filteredTickets.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={12} align="center">
                    <Typography variant="body2" color="textSecondary">
                      No TSI tickets found. {isAdmin ? 'Enter an ADO query and click "Get Latest" to fetch tickets.' : 'Contact an administrator to fetch data.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredTickets.map((ticket, index) => {
                const linkedBugs = linkedBugsMap[ticket.id] || [];
                return (
                  <TableRow key={ticket._id || index} hover>
                    <TableCell>
                      {ticket.id ? (
                        <Typography
                          component="a"
                          href={getAdoUrl(ticket.id)}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="body2"
                          sx={{
                            color: '#1976d2',
                            textDecoration: 'none',
                            '&:hover': {
                              textDecoration: 'underline',
                              color: '#1565c0'
                            }
                          }}
                        >
                          {ticket.id}
                        </Typography>
                      ) : (
                        'N/A'
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={ticket.title || 'No title'} arrow>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {ticket.title || 'N/A'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      {ticket.workItemType && (
                        <Chip 
                          label={ticket.workItemType} 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {ticket.state && (
                        <Chip 
                          label={ticket.state} 
                          size="small" 
                          color="secondary" 
                          variant="outlined"
                        />
                      )}
                    </TableCell>
                    <TableCell>{ticket.priority || 'N/A'}</TableCell>
                    <TableCell>{ticket.team || 'N/A'}</TableCell>
                    <TableCell>{ticket.environment || 'N/A'}</TableCell>
                    <TableCell>{formatDate(ticket.createdDate)}</TableCell>
                    <TableCell>{formatDate(ticket.activatedDate)}</TableCell>
                    <TableCell>{formatDate(ticket.closedDate)}</TableCell>
                    <TableCell>
                      {ticket.age !== undefined && ticket.age !== null ? (
                        <Chip 
                          label={`${ticket.age} days`} 
                          size="small" 
                          color={ticket.age > 30 ? 'error' : ticket.age > 14 ? 'warning' : 'success'}
                          variant="outlined"
                        />
                      ) : (
                        'N/A'
                      )}
                    </TableCell>
                    <TableCell>
                      {linkedBugs.length > 0 ? (
                        <Tooltip 
                          title={
                            <Box>
                              {linkedBugs.map((bug, idx) => (
                                <Typography key={idx} variant="caption" display="block">
                                  Bug {bug.id}: {bug.title}
                                </Typography>
                              ))}
                            </Box>
                          }
                          arrow
                        >
                          <Chip 
                            label={`${linkedBugs.length} bug${linkedBugs.length !== 1 ? 's' : ''}`}
                            size="small"
                            color="info"
                            variant="outlined"
                            sx={{ cursor: 'help' }}
                          />
                        </Tooltip>
                      ) : (
                        'None'
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderTop: 1, borderColor: 'divider' }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Group Filter</InputLabel>
            <Select
              value={filters.group}
              onChange={(e) => handleFilterChange('group', e.target.value)}
              label="Group Filter"
              disabled={loadingEmailGroups}
            >
              <MenuItem value="">All Groups</MenuItem>
              <MenuItem value="SRE Group">SRE Group</MenuItem>
              <MenuItem value="Professional Service Group">Professional Service Group</MenuItem>
              <MenuItem value="Client Support Team">Client Support Team</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
          </FormControl>
          
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={filters.group ? filteredTickets.length : totalCount}
            rowsPerPage={rowsPerPage}
            page={filters.group ? Math.min(page, Math.floor((filteredTickets.length - 1) / rowsPerPage)) : page}
            onPageChange={(event, newPage) => {
              if (!filters.group) {
                handleChangePage(event, newPage);
              } else {
                setPage(newPage);
              }
            }}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Box>
      </Paper>
    );
  };
  
  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box display="flex" alignItems="center">
          <InvestigationIcon sx={{ mr: 1, color: "#1D4434" }} />
          <Typography variant="h4" gutterBottom>
            TSI Dashboard
          </Typography>
        </Box>
        
        <Box display="flex" gap={1}>
          <Button
            startIcon={<RefreshIcon />}
            onClick={() => {
              fetchTickets();
              fetchStats();
              fetchColumns();
            }}
            disabled={loading}
          >
            Refresh
          </Button>
          
          {isAdmin && (
            <>
              <Button
                variant="contained"
                startIcon={fetchingBugs ? <CircularProgress size={20} /> : <InvestigationIcon />}
                onClick={handleGetLinkedBugs}
                disabled={fetchingBugs}
                sx={{ bgcolor: "#1D4434", '&:hover': { bgcolor: "#2D5544" } }}
              >
                {fetchingBugs ? 'Fetching...' : 'Get Linked Bugs'}
              </Button>
              <TextField
                size="small"
                placeholder="ADO query: saved-query GUID or WIQL"
                value={adoQuery}
                onChange={(e) => setAdoQuery(e.target.value)}
                disabled={fetchingAdo}
                sx={{ minWidth: 320, bgcolor: 'background.paper' }}
              />
              <Button
                variant="contained"
                startIcon={fetchingAdo ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                onClick={handleFetchFromAdo}
                disabled={fetchingAdo || !adoQuery.trim()}
                sx={{ bgcolor: "#1D4434", '&:hover': { bgcolor: "#2D5544" } }}
              >
                {fetchingAdo ? 'Fetching…' : 'Get Latest'}
              </Button>
            </>
          )}
        </Box>
      </Box>

      <Box mb={3}>
        <Typography variant="body1" color="textSecondary">
          Investigation work items dashboard — fetch the latest from Azure DevOps using a query
        </Typography>
      </Box>
      
      {/* Error and Success Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
      
      {/* Statistics Cards */}
      {renderStatsCards()}
      
      {/* Charts Section */}
      <TsiCharts />
      
      {/* Filters */}
      {renderFilters()}
      
      {/* Data Table */}
      {renderDataTable()}
    </Box>
  );
};

export default TsiDashboard;
